export type DeployDocSection = {
  id:
    | "wordpress"
    | "webflow"
    | "wix"
    | "squarespace"
    | "shopify"
    | "custom-html"
    | "iframe";
  title: string;
  description?: string;
  steps: string[];
  code?: string;
  tips?: string[];
};

const DEFAULT_CLINIC_SDK_KEY = "YOUR_UNIQUE_CLINIC_KEY";

export const getDeployDocSections = (
  clinicSdkKey?: string,
  widgetScriptSnippet?: string,
  standaloneFormUrl?: string,
  deployMode: "widget" | "iframe" = "widget",
  includeStandaloneEmbedSection: boolean = true,
): DeployDocSection[] => {
  const resolvedKey = clinicSdkKey?.trim() || DEFAULT_CLINIC_SDK_KEY;
  const iframeSourceUrl =
    standaloneFormUrl?.trim() ||
    `https://form.clinicos.ai/onboarding-single-procedure?clinicSdkKey=${resolvedKey}`;
  const embedCode =
    widgetScriptSnippet?.trim() ||
    `<script
  src="https://www.clinic-os.com/clinicos-full-form-prod-v2.js"
  clinicSdkKey="${resolvedKey}"
  formName="onboarding-tier1"
></script>`;

  const sections: DeployDocSection[] = [];

  if (includeStandaloneEmbedSection) {
    sections.push({
      id: "iframe",
      title: "Embed on a standalone page (instead of widget)",
      description:
        "Best when you want the form to appear inside a specific page on your website.",
      steps: [
        "Open the page editor for your site.",
        "Add a Custom HTML block or HTML embed.",
        "Paste the iframe below.",
      ],
      code: `<iframe
  src="${iframeSourceUrl}"
  width="100%"
  height="900"
  style="border: none;"
  loading="lazy"
  allowfullscreen
></iframe>`,
      tips: ["Adjust height if the form is shorter or longer."],
    });
  }

  sections.push({
    id: "custom-html",
    title: "Generic HTML Website",
    steps: [
      "Open the HTML template for your website.",
      `Paste the ${deployMode === "iframe" ? "iframe embed code" : "script embed code"} below before the closing </body> tag.`,
      "Deploy your site changes.",
      "Open the public page and confirm the form or widget is visible.",
    ],
    code: embedCode,
    tips: [
      "If your site uses a strict Content Security Policy, allow scripts from clinic-os.com.",
    ],
  });

  sections.push({
    id: "wordpress",
    title: "WordPress",
    description:
      "Use WPCode for the fastest setup, or add the same embed in your theme footer.",
    steps: [
      "WordPress Admin → Plugins → Add New.",
      "Install and activate WPCode (Insert Headers and Footers), then open Code Snippets → Header & Footer.",
      `Paste the ${deployMode === "iframe" ? "iframe embed code" : "script embed code"} below into the Body/Footer section and save.`,
      "Alternative: Appearance → Theme File Editor → functions.php, add a wp_footer hook that outputs the same embed code.",
      "Open your public website and confirm the form or widget loads.",
    ],
    code: embedCode,
    tips: [
      "If your site uses caching or optimization plugins, clear cache before validating.",
      "For page-specific placement, use a Custom HTML block instead of a site-wide footer.",
    ],
  });

  sections.push({
    id: "webflow",
    title: "Webflow",
    steps: [
      "Webflow Dashboard → Site Settings → Custom Code.",
      `Paste the ${deployMode === "iframe" ? "iframe embed code" : "script embed code"} below into Footer Code.`,
      "Save, then Publish to your staging and production domains.",
      "Refresh the live site and confirm the form or widget is visible.",
    ],
    code: embedCode,
    tips: [
      "Custom code runs only on published domains, not in the Designer preview.",
    ],
  });

  sections.push({
    id: "wix",
    title: "Wix",
    steps: [
      "Make sure your Wix site is published and connected to a domain.",
      "Wix Dashboard → Settings → Custom Code.",
      "Click Add Custom Code and paste the embed code below.",
      "Set placement to Body - end, and apply to all pages or selected pages.",
      "Publish your site and verify the form or widget loads.",
    ],
    code: embedCode,
    tips: [
      "If adding to selected pages, ensure the page where patients land is included.",
    ],
  });

  sections.push({
    id: "squarespace",
    title: "Squarespace",
    steps: [
      "Squarespace → Settings → open the Code Injection panel.",
      `Paste the ${deployMode === "iframe" ? "iframe embed code" : "script embed code"} below into Footer.`,
      "Save changes and refresh your live website.",
      "Confirm the form or widget appears and can be submitted.",
    ],
    code: embedCode,
    tips: [
      "For one-page placement, use a Code Block inside the page editor instead of global injection.",
    ],
  });

  sections.push({
    id: "shopify",
    title: "Shopify",
    steps: [
      "Shopify Admin → Online Store → Themes.",
      "On your active theme, open the actions menu (three dots) → Edit code.",
      "Open layout/theme.liquid and paste the embed code before </body> (or use a Custom Liquid section for specific pages).",
      "Save, then preview/publish the theme.",
      "Visit the storefront and validate the form or widget.",
    ],
    code: embedCode,
    tips: [
      "If your store has a custom app block strategy, place this in the global footer area for consistent rendering.",
    ],
  });

  if (deployMode === "widget") {
    sections.push({
      id: "iframe",
      title: "Need inline embed instead of floating widget?",
      description:
        "Use this option when you want the full form embedded directly inside a page section.",
      steps: [
        "Switch to Form Format and choose the in-page form option.",
        "Copy the iframe embed code generated by Clinicos.",
        "Paste that iframe into any CMS Custom HTML block.",
      ],
      code: `<iframe
  src="${iframeSourceUrl}"
  width="100%"
  height="900"
  style="border: none;"
  loading="lazy"
  allowfullscreen
></iframe>`,
    });
  }

  return sections;
};
